import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Accordion } from '../../elements/Accordion';
import CustomLink from '../../CustomLink';
import Header from '../../../Layout/Header';
import IngredientsSlider from '../../Home/Slider/IngredientsSlider';
import useMeta from '../../../hooks/useMeta';
import { logo_home } from '../../../Data/Images/Home';
import '../../../assets/css/landing-pages.css';
import HeroImg from '../../../assets/referral-hero.webp';

const K = {
  MetaTitle:        'LP_Ref_MetaTitle',
  MetaDesc:         'LP_Ref_MetaDesc',
  HeroLabel:        'LP_Ref_HeroLabel',
  HeroH1a:          'LP_Ref_HeroH1a',
  HeroH1b:          'LP_Ref_HeroH1b',
  HeroSubline:      'LP_Ref_HeroSubline',
  HeroCtaPrimary:   'LP_Ref_HeroCtaPrimary',
  HeroCtaSecondary: 'LP_Ref_HeroCtaSecondary',
  QuoteA:           'LP_Ref_QuoteA',
  QuoteB:           'LP_Ref_QuoteB',
  HeroImgAlt:       'LP_Ref_HeroImgAlt',
  BenefitLabel:     'LP_Ref_BenefitLabel',
  Benefit1Val:      'LP_Ref_Benefit1Val',
  Benefit1Title:    'LP_Ref_Benefit1Title',
  Benefit1Desc:     'LP_Ref_Benefit1Desc',
  Benefit2Val:      'LP_Ref_Benefit2Val',
  Benefit2Title:    'LP_Ref_Benefit2Title',
  Benefit2Desc:     'LP_Ref_Benefit2Desc',
  HowLabel:         'LP_Ref_HowLabel',
  HowHeading:       'LP_Ref_HowHeading',
  HowIntro:         'LP_Ref_HowIntro',
  Step1Title:       'LP_Ref_Step1Title',
  Step1Desc:        'LP_Ref_Step1Desc',
  Step2Title:       'LP_Ref_Step2Title',
  Step2Desc:        'LP_Ref_Step2Desc',
  Step3Title:       'LP_Ref_Step3Title',
  Step3Desc:        'LP_Ref_Step3Desc',
  PathsLabel:       'LP_Ref_PathsLabel',
  CodeTitle:        'LP_Ref_CodeTitle',
  CodeDesc:         'LP_Ref_CodeDesc',
  CodeCta:          'LP_Ref_CodeCta',
  RedeemTitle:      'LP_Ref_RedeemTitle',
  RedeemDesc:       'LP_Ref_RedeemDesc',
  RedeemCta:        'LP_Ref_RedeemCta',
  FaqHeading:       'LP_Ref_FaqHeading',
  Faq1Q: 'LP_Ref_Faq1Q', Faq1A: 'LP_Ref_Faq1A',
  Faq2Q: 'LP_Ref_Faq2Q', Faq2A: 'LP_Ref_Faq2A',
  Faq3Q: 'LP_Ref_Faq3Q', Faq3A: 'LP_Ref_Faq3A',
  Faq4Q: 'LP_Ref_Faq4Q', Faq4A: 'LP_Ref_Faq4A',
  Faq5Q: 'LP_Ref_Faq5Q', Faq5A: 'LP_Ref_Faq5A',
  Faq6Q: 'LP_Ref_Faq6Q', Faq6A: 'LP_Ref_Faq6A',
  Faq7Q: 'LP_Ref_Faq7Q', Faq7A: 'LP_Ref_Faq7A',
  Faq8Q: 'LP_Ref_Faq8Q', Faq8A: 'LP_Ref_Faq8A',
  Faq9Q: 'LP_Ref_Faq9Q', Faq9A: 'LP_Ref_Faq9A',
  Faq10Q: 'LP_Ref_Faq10Q', Faq10A: 'LP_Ref_Faq10A',
  ProductLabel:     'LP_Ref_ProductLabel',
  ProductHeading:   'LP_Ref_ProductHeading',
  ProductSub:       'LP_Ref_ProductSub',
  FinalHeading:     'LP_Ref_FinalHeading',
  FinalSubline:     'LP_Ref_FinalSubline',
  FinalCtaPrimary:  'LP_Ref_FinalCtaPrimary',
  FinalCtaSecondary:'LP_Ref_FinalCtaSecondary',
  CtaSub:           'LP_Ref_CtaSub',
};

const FAQ_KEYS = [
  { q: K.Faq1Q, a: K.Faq1A },
  { q: K.Faq2Q, a: K.Faq2A },
  { q: K.Faq3Q, a: K.Faq3A },
  { q: K.Faq4Q, a: K.Faq4A },
  { q: K.Faq5Q, a: K.Faq5A },
  { q: K.Faq6Q, a: K.Faq6A },
  { q: K.Faq7Q, a: K.Faq7A },
  { q: K.Faq8Q, a: K.Faq8A },
  { q: K.Faq9Q, a: K.Faq9A },
  { q: K.Faq10Q, a: K.Faq10A },
];

const SERIF = { fontFamily: "'DM Serif Display', serif" };
const SANS  = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

// Member intent: log in and land straight on the referral-code page.
const CODE_LINK = '/login?redirect=/dashboard/referralcode';
// Prospect intent: start the quiz. No €20 claim — the visitor here has no referral code.
const TEST_LINK = '/quiz';

// Butter, filled — the loud CTA. Fires a Meta event only when `event` is passed.
const ButterCta = ({ children, to = CODE_LINK, event, full = true }) => {
  const { metaEvent } = useMeta();
  return (
    <CustomLink
      to={to}
      onClick={event ? () => metaEvent(event) : undefined}
      className="lp-btn-cta"
      style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, background: '#F6E147', color: '#103642',
        fontWeight: 700, fontSize: 16, borderRadius: 999,
        padding: '18px 36px', textDecoration: 'none',
        width: full ? '100%' : 'auto' }}
    >
      {children}
      <span aria-hidden="true">→</span>
    </CustomLink>
  );
};

// Ghost outline — the quieter CTA.
const SecondaryCta = ({ children, to = TEST_LINK, event, full = true, dark = false }) => {
  const { metaEvent } = useMeta();
  return (
    <CustomLink
      to={to}
      onClick={event ? () => metaEvent(event) : undefined}
      style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, background: 'transparent',
        color: dark ? '#F9F9F9' : '#103642',
        border: `1px solid ${dark ? 'rgba(255,255,255,0.35)' : 'rgba(16,54,66,0.25)'}`,
        fontWeight: 600, fontSize: 16, borderRadius: 999,
        padding: '17px 36px', textDecoration: 'none',
        width: full ? '100%' : 'auto' }}
    >
      {children}
    </CustomLink>
  );
};

const Label = ({ children, pill = false }) => {
  const base = {
    ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
    textTransform: 'uppercase', color: '#116D6E',
    display: 'block', textAlign: 'center', margin: 0,
  };
  if (pill) return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ ...base, background: 'rgba(17,109,110,0.1)', borderRadius: 999, padding: '6px 16px' }}>
        {children}
      </span>
    </div>
  );
  return <p style={base}>{children}</p>;
};

const STEP_ICONS = {
  share: <><path d="M22 2 11 13" /><path d="M22 2 15 22 11 13 2 9 22 2Z" /></>,
  discover: (
    <>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96 12 12.01l8.73-5.05" />
      <path d="M12 22.08V12" />
    </>
  ),
  gift: (
    <>
      <path d="M20 12v10H4V12" />
      <path d="M2 7h20v5H2z" />
      <path d="M12 22V7" />
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </>
  ),
};

const StepIcon = ({ name }) => (
  <div style={{ flexShrink: 0, width: 48, height: 48, borderRadius: '50%',
    background: '#103642', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F6E147"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {STEP_ICONS[name]}
    </svg>
  </div>
);

const Section = ({ bg = '#fff', py = 64, children, style = {}, maxW = 480, px = 24 }) => (
  <section style={{ background: bg, padding: `${py}px ${px}px`, ...style }}>
    <div style={{ maxWidth: maxW, margin: '0 auto' }}>
      {children}
    </div>
  </section>
);

const Stack = ({ gap = 24, align = 'stretch', children, style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, alignItems: align, ...style }}>
    {children}
  </div>
);

const Referral = () => {
  const { t } = useTranslation();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 1024
  );

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxW = isDesktop ? 1080 : 480;
  const px = isDesktop ? 80 : 24;

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_KEYS.map(({ q, a }) => ({
      '@type': 'Question',
      name: t(q),
      acceptedAnswer: { '@type': 'Answer', text: t(a) },
    })),
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff', ...SANS }}>
      <Helmet>
        <title>{t(K.MetaTitle)}</title>
        <meta name="description" content={t(K.MetaDesc)} />
        <link rel="canonical" href="https://www.weriise.co/referral" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t(K.MetaTitle)} />
        <meta property="og:description" content={t(K.MetaDesc)} />
        <meta property="og:url" content="https://www.weriise.co/referral" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      </Helmet>

      {/* ── Header (same as Home) ── */}
      <Header />

      {/* ── Hero ── */}
      {isDesktop ? (
        <section style={{ background: '#F9F9F9', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto',
            display: 'grid', gridTemplateColumns: '54% 46%', minHeight: 520 }}>
            <div style={{ padding: '64px 56px 64px 80px', display: 'flex', alignItems: 'center' }}>
              <Stack gap={24} style={{ width: '100%', maxWidth: 520 }}>
                <p style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: '#116D6E', margin: 0 }}>
                  {t(K.HeroLabel)}
                </p>
                <h1 style={{ ...SERIF, fontSize: 56, lineHeight: 1.06, fontWeight: 400,
                  color: '#103642', margin: 0 }}>
                  {t(K.HeroH1a)}<br />
                  <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1b)}</em>
                </h1>
                <p style={{ ...SANS, fontSize: 18, color: 'rgba(16,54,66,0.7)',
                  lineHeight: 1.7, margin: 0 }}>
                  {t(K.HeroSubline)}
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <ButterCta full={false}>{t(K.HeroCtaPrimary)}</ButterCta>
                  <SecondaryCta full={false} event="Lead">{t(K.HeroCtaSecondary)}</SecondaryCta>
                </div>
              </Stack>
            </div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center',
              justifyContent: 'center', padding: 32 }}>
              <img src={HeroImg} alt={t(K.HeroImgAlt)}
                style={{ width: '100%', maxWidth: 440, height: 'auto', display: 'block',
                  borderRadius: 20, boxShadow: '0 20px 40px rgba(16,54,66,0.15)' }}
                loading="eager" />
            </div>
          </div>
        </section>
      ) : (
        <section style={{ background: '#F9F9F9' }}>
          <div style={{ maxWidth: 480, margin: '0 auto', padding: '28px 24px 8px' }}>
            <Stack gap={20}>
              <p style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: '#116D6E', margin: 0 }}>
                {t(K.HeroLabel)}
              </p>
              <h1 style={{ ...SERIF, fontSize: 40, lineHeight: 1.08, fontWeight: 400,
                color: '#103642', margin: 0 }}>
                {t(K.HeroH1a)}{' '}
                <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1b)}</em>
              </h1>
              <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.7)',
                lineHeight: 1.7, margin: 0 }}>
                {t(K.HeroSubline)}
              </p>
            </Stack>
          </div>
          <img src={HeroImg} alt={t(K.HeroImgAlt)}
            style={{ width: 'calc(100% - 48px)', maxWidth: 420, height: 'auto', display: 'block',
              margin: '20px auto 0', borderRadius: 16,
              boxShadow: '0 16px 32px rgba(16,54,66,0.15)' }} loading="eager" />
          <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 24px 36px' }}>
            <Stack gap={12}>
              <ButterCta>{t(K.HeroCtaPrimary)}</ButterCta>
              <SecondaryCta event="Lead">{t(K.HeroCtaSecondary)}</SecondaryCta>
            </Stack>
          </div>
        </section>
      )}

      {/* ── Editorial statement ── */}
      <section style={{ background: '#fff', padding: `${isDesktop ? 88 : 64}px ${px}px` }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <p style={{ ...SERIF, fontSize: isDesktop ? 34 : 26, lineHeight: 1.3, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.QuoteA)}{' '}
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.QuoteB)}</em>
          </p>
        </div>
      </section>

      {/* ── Benefit split (You get / Friend gets) ── */}
      <Section bg="#103642" maxW={maxW} px={px}>
        <Stack gap={32}>
          <Label>{t(K.BenefitLabel)}</Label>
          <div style={{
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? '1fr 1fr' : undefined,
            flexDirection: isDesktop ? undefined : 'column',
            gap: 20,
          }}>
            {[
              { val: K.Benefit1Val, title: K.Benefit1Title, desc: K.Benefit1Desc },
              { val: K.Benefit2Val, title: K.Benefit2Title, desc: K.Benefit2Desc },
            ].map(({ val, title, desc }) => (
              <div key={title} style={{ background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16,
                padding: isDesktop ? 36 : 28 }}>
                <Stack gap={10}>
                  <p style={{ ...SERIF, fontSize: 56, lineHeight: 1, fontWeight: 400,
                    color: '#F6E147', margin: 0 }}>{t(val)}</p>
                  <p style={{ ...SANS, fontSize: 17, fontWeight: 700,
                    color: '#F9F9F9', margin: 0 }}>{t(title)}</p>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(255,255,255,0.65)',
                    lineHeight: 1.6, margin: 0 }}>{t(desc)}</p>
                </Stack>
              </div>
            ))}
          </div>
        </Stack>
      </Section>

      {/* ── How it works ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={isDesktop ? 48 : 40}>
          <Stack gap={16} align="center" style={{ textAlign: 'center' }}>
            <Label>{t(K.HowLabel)}</Label>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
              color: '#103642', margin: 0 }}>
              {t(K.HowHeading)}
            </h2>
            <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.6)', lineHeight: 1.7,
              margin: 0, maxWidth: 620 }}>
              {t(K.HowIntro)}
            </p>
          </Stack>
          {isDesktop ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 32 }}>
              {[
                { icon: 'share', title: K.Step1Title, desc: K.Step1Desc },
                { icon: 'discover', title: K.Step2Title, desc: K.Step2Desc },
                { icon: 'gift', title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ icon, title, desc }) => (
                <div key={icon} className="lp-card" style={{ background: '#f5f7f7',
                  borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <StepIcon name={icon} />
                  <p style={{ ...SANS, fontSize: 17, fontWeight: 600, color: '#103642', margin: 0 }}>
                    {t(title)}
                  </p>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                    margin: 0, lineHeight: 1.65 }}>{t(desc)}</p>
                </div>
              ))}
            </div>
          ) : (
            <Stack gap={32}>
              {[
                { icon: 'share', title: K.Step1Title, desc: K.Step1Desc },
                { icon: 'discover', title: K.Step2Title, desc: K.Step2Desc },
                { icon: 'gift', title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ icon, title, desc }) => (
                <div key={icon} style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                  <StepIcon name={icon} />
                  <Stack gap={8}>
                    <p style={{ ...SANS, fontSize: 16, fontWeight: 600, color: '#103642', margin: 0 }}>
                      {t(title)}
                    </p>
                    <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                      margin: 0, lineHeight: 1.65 }}>{t(desc)}</p>
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      </Section>

      {/* ── Two paths: get a code / redeem a code ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={24}>
          <Label pill>{t(K.PathsLabel)}</Label>
          <div style={{
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? '1fr 1fr' : undefined,
            flexDirection: isDesktop ? undefined : 'column',
            gap: 20,
          }}>
            {[
              { title: K.CodeTitle, desc: K.CodeDesc, cta: K.CodeCta, to: CODE_LINK, butter: true },
              { title: K.RedeemTitle, desc: K.RedeemDesc, cta: K.RedeemCta, to: TEST_LINK, butter: false },
            ].map(({ title, desc, cta, to, butter }) => (
              <div key={title} style={{ background: '#fff', borderRadius: 16,
                padding: isDesktop ? 36 : 28, border: '1px solid rgba(17,109,110,0.1)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Stack gap={10} style={{ flex: 1 }}>
                  <p style={{ ...SERIF, fontSize: 24, lineHeight: 1.15, fontWeight: 400,
                    color: '#103642', margin: 0 }}>{t(title)}</p>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.65)',
                    lineHeight: 1.65, margin: 0 }}>{t(desc)}</p>
                </Stack>
                {butter
                  ? <ButterCta to={to}>{t(cta)}</ButterCta>
                  : <SecondaryCta to={to} event="Lead">{t(cta)}</SecondaryCta>}
              </div>
            ))}
          </div>
        </Stack>
      </Section>

      {/* ── FAQ ── */}
      <Section bg="#fff" maxW={isDesktop ? 800 : 480} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0 }}>
            {t(K.FaqHeading)}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid #f0f0f0' }}>
            {FAQ_KEYS.map(({ q, a }) => (
              <div key={q} className="lp-faq-row" style={{ borderBottom: '1px solid #f0f0f0', padding: '4px 0' }}>
                <Accordion heading={t(q)}>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.7)',
                    lineHeight: 1.65, margin: '12px 0 16px 0' }}>
                    {t(a)}
                  </p>
                </Accordion>
              </div>
            ))}
          </div>
        </Stack>
      </Section>

      {/* ── Product teaser ── */}
      <section style={{ background: '#f5f7f7', paddingTop: isDesktop ? 72 : 56,
        paddingBottom: isDesktop ? 48 : 36 }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `0 ${px}px` }}>
          <Stack gap={12} align="center" style={{ textAlign: 'center' }}>
            <Label pill>{t(K.ProductLabel)}</Label>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
              color: '#103642', margin: 0 }}>
              {t(K.ProductHeading)}
            </h2>
            <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.6)', lineHeight: 1.7,
              margin: 0, maxWidth: 560 }}>
              {t(K.ProductSub)}
            </p>
          </Stack>
        </div>
        <div style={{ marginTop: 32 }}>
          <IngredientsSlider />
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ background: '#103642', padding: `80px ${px}px` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 40 : 28, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0 }}>
              {t(K.FinalHeading)}
            </h2>
            <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.7)',
              margin: 0, lineHeight: 1.7, maxWidth: isDesktop ? 560 : undefined }}>
              {t(K.FinalSubline)}
            </p>
            <Stack gap={12} style={{ width: isDesktop ? 380 : '100%' }}>
              <ButterCta>{t(K.FinalCtaPrimary)}</ButterCta>
              <SecondaryCta dark event="Lead">{t(K.FinalCtaSecondary)}</SecondaryCta>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.4)',
                textAlign: 'center', margin: 0 }}>
                {t(K.CtaSub)}
              </p>
            </Stack>
          </Stack>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: '#fff', borderTop: '1px solid #f0f0f0',
        padding: isDesktop ? '40px 80px' : '40px 24px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={16} align="center">
            <img src={logo_home} alt="riise" style={{ height: 20, width: 'auto', opacity: 0.4 }} />
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              <CustomLink to="/impressum"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Impressum
              </CustomLink>
              <CustomLink to="/policy"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Privacy Policy
              </CustomLink>
            </div>
            <p style={{ ...SANS, fontSize: 11, color: 'rgba(16,54,66,0.3)',
              textAlign: 'center', margin: 0 }}>
              {t('LP_SupplementDisclaimer')}
            </p>
          </Stack>
        </div>
      </footer>
    </div>
  );
};

export default Referral;
